import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mergeControllerSummary } from './mergeControllerSummary';
import { apiFetch, readJsonResponse } from '../api/client';
import InfoHint from '../components/InfoHint';
import {
  commentDeviceLabel,
  resolveCommentDeviceId,
} from '../components/DeviceCommentsPanel';
import RiskCaseCard from '../components/RiskCaseCard';
import RiskCaseDetailModal from '../components/RiskCaseDetailModal';
import RiskCasesTable from '../components/RiskCasesTable';
import DeviceHistoryModal from '../components/DeviceHistoryModal';
import DeviceCommentsModal from '../components/DeviceCommentsModal';
import DashboardRisksSkeleton from '../components/skeleton/DashboardRisksSkeleton';
import {
  DEAD_DEVICE_MONTH_OPTIONS,
  formatDeadDeviceMonthOption,
} from '../constants/deadDeviceThreshold';
import {
  getAvailableRiskTypes,
  getDefaultRiskTypesCsv,
  getRiskShortLabel,
} from '../constants/riskTypes';
import FilterPresetsBar from '../components/FilterPresetsBar';
import FilterPresetsModal from '../components/FilterPresetsModal';
import RiskHistoryModal from '../components/RiskHistoryModal';
import RisksTopBar from '../components/RisksTopBar';
import { listFilterPresets } from '../api/filterPresets';
import { useSkeletonVisibility } from '../hooks/useSkeletonVisibility';
import { useOpenClientLk } from '../hooks/useOpenClientLk';
import { buildRiskCases, casesForDashboardView, paginateCases } from './buildRiskCases';
import {
  buildDashboardSearchParams,
  buildRiskQuery,
  DEFAULT_FILTERS,
  FILTER_FETCH_DEBOUNCE_MS,
  MULTI_RISK_FETCH_CAP,
  OVERDUE_PRESETS,
  PAGE_SIZE_OPTIONS,
  parseDashboardQuery,
  SERIAL_SEARCH_DEBOUNCE_MS,
  serializeDashboardFetchKey,
  serializeDashboardQuery,
} from './dashboardQuery';
import { cacheFetch, cacheInvalidate } from '../api/queryCache';

const RISK_LEVEL_MULTI_OPTIONS = [
  { value: 'very_high', label: 'Оч. высокий', short: 'Оч.в' },
  { value: 'high', label: 'Высокий', short: 'Выс' },
  { value: 'medium', label: 'Средний', short: 'Ср' },
  { value: 'low', label: 'Низкий', short: 'Низ' },
  { value: 'ok', label: 'Без риска', short: 'Ок' },
];

function parseCsvList(value) {
  if (!value) {
    return [];
  }

  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseSelectedRiskTypes(riskTypesCsv) {
  const availableIds = getAvailableRiskTypes().map((risk) => risk.id);
  const selected = parseCsvList(riskTypesCsv).filter((id) => availableIds.includes(id));

  if (selected.length === 0) {
    return availableIds;
  }

  return availableIds.filter((id) => selected.includes(id));
}

function toggleRiskTypeCsv(currentValue, riskId) {
  const availableIds = getAvailableRiskTypes().map((risk) => risk.id);
  const selected = new Set(parseCsvList(currentValue).filter((id) => availableIds.includes(id)));

  if (selected.has(riskId)) {
    selected.delete(riskId);
  } else {
    selected.add(riskId);
  }

  if (selected.size === 0) {
    return getDefaultRiskTypesCsv();
  }

  return availableIds.filter((id) => selected.has(id)).join(',');
}

function parseRiskLevelCsv(value) {
  return parseCsvList(value);
}

function toggleRiskLevelCsv(currentValue, level) {
  const selected = new Set(parseRiskLevelCsv(currentValue));

  if (selected.has(level)) {
    selected.delete(level);
  } else {
    selected.add(level);
  }

  return RISK_LEVEL_MULTI_OPTIONS.filter((option) => selected.has(option.value))
    .map((option) => option.value)
    .join(',');
}

function toggleFirmwareVersionCsv(currentValue, version) {
  return toggleCsvListValue(currentValue, version);
}

function toggleCsvListValue(currentValue, item) {
  const selected = parseCsvList(currentValue);
  const next = selected.includes(item) ? selected.filter((value) => value !== item) : [...selected, item];
  return next.join(',');
}

function csvMultiSelectLabel(selected, emptyLabel, labelByValue = {}) {
  if (selected.length === 0) {
    return emptyLabel;
  }

  const display = selected.map((value) => labelByValue[value] || value);

  if (display.length === 1) {
    return display[0];
  }

  if (display.length >= 4) {
    return `${display.length} выбрано`;
  }

  return `${display[0]} +${display.length - 1}`;
}

const SORT_OPTIONS = [
  { value: 'risk_score_desc', label: 'Риск: выше → ниже' },
  { value: 'risk_score_asc', label: 'Риск: ниже → выше' },
  { value: 'intervention_priority_desc', label: 'Приоритет вмешательства' },
  { value: 'days_overdue_desc', label: 'Просрочка: больше → меньше' },
  { value: 'days_overdue_asc', label: 'Просрочка: меньше → больше' },
];

const SCHEDULE_OPTIONS = [
  { value: '', label: 'Все графики' },
  { value: 'daily', label: 'День' },
  { value: 'weekly', label: 'Неделя' },
  { value: 'monthly', label: 'Месяц' },
];

const INTEGRATION_OPTIONS = [
  { value: '', label: 'Всё' },
  { value: 'true', label: 'С интеграцией' },
  { value: 'company', label: 'С передачей в компанию' },
  { value: 'false', label: 'Без интеграции' },
];

const INTEGRATION_UNAVAILABLE_OPTION = { value: '', label: 'Нет доступа' };

const CONNECTION_TYPE_OPTIONS = [
  { value: '', label: 'Все связи' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'nbiot', label: 'NB-IoT' },
];

const OVERDUE_PRESET_BUTTONS = OVERDUE_PRESETS;

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS;

function FilterField({ label, children, className = '' }) {
  const fieldId = `filter-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className}`}>
      <label htmlFor={fieldId} className="text-[10px] leading-none text-slate-500">
        {label}
      </label>
      {typeof children === 'function' ? children(fieldId) : children}
    </div>
  );
}

function FilterSelect({ id, value, onChange, disabled, options, selectClassName }) {
  return (
    <select id={id} value={value} disabled={disabled} onChange={onChange} className={selectClassName}>
      {options.map((option) => (
        <option key={option.value || 'all'} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function SerialSearchInput({ id, value, disabled, onChange, inputClassName }) {
  const [draft, setDraft] = useState(value || '');
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (draft === (value || '')) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onChangeRef.current(draft);
    }, SERIAL_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [draft, value]);

  return (
    <input
      id={id}
      type="search"
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      placeholder="WFKB00000…"
      className={inputClassName}
    />
  );
}

function HScrollFade({ id, children, className = '', role, 'aria-label': ariaLabel }) {
  return (
    <div id={id} className={`relative min-w-0 ${className}`} role={role} aria-label={ariaLabel}>
      <div className="flex flex-nowrap gap-1 overflow-x-auto pb-0.5 [scrollbar-width:thin]">{children}</div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-l from-white to-transparent dark:from-slate-950/90"
      />
    </div>
  );
}

function RiskLevelMultiFilter({ id, value, disabled, onChange }) {
  const selected = new Set(parseRiskLevelCsv(value));

  return (
    <HScrollFade id={id}>
      {RISK_LEVEL_MULTI_OPTIONS.map((option) => {
        const checked = selected.has(option.value);
        const checkboxId = `${id}-${option.value}`;

        return (
          <label
            key={option.value}
            htmlFor={checkboxId}
            title={option.label}
            className={`inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] leading-5 sm:min-h-0 sm:px-1 sm:py-0 ${
              checked
                ? 'border-violet-400/40 bg-violet-400/10 text-violet-800 dark:text-violet-100'
                : 'border-slate-300 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              id={checkboxId}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(toggleRiskLevelCsv(value, option.value))}
              className="h-2.5 w-2.5 rounded border-slate-300 bg-white text-violet-400 focus:ring-violet-400/40 dark:border-slate-600 dark:bg-slate-900"
            />
            {option.short || option.label}
          </label>
        );
      })}
    </HScrollFade>
  );
}

function riskTypeMultiSelectLabel(selectedIds) {
  const availableIds = getAvailableRiskTypes().map((risk) => risk.id);

  if (selectedIds.length === 0 || selectedIds.length === availableIds.length) {
    return 'Все';
  }

  return selectedIds.map((id) => `#${id}`).join(', ');
}

function RiskTypeMultiFilter({ id, value, disabled, onChange }) {
  const available = getAvailableRiskTypes();
  const selectedIds = parseSelectedRiskTypes(value);
  const selected = new Set(selectedIds);
  const allSelected = selectedIds.length === available.length;

  return (
    <HScrollFade id={id} role="group" aria-label="Типы риска">
      <label
        htmlFor={`${id}-all`}
        className={`inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] leading-5 sm:min-h-0 sm:px-1 sm:py-0 ${
          allSelected
            ? 'border-sky-400/40 bg-sky-400/10 text-sky-800 dark:text-sky-100'
            : 'border-slate-300 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <input
          id={`${id}-all`}
          type="checkbox"
          checked={allSelected}
          disabled={disabled}
          onChange={() => onChange(getDefaultRiskTypesCsv())}
          className="h-2.5 w-2.5 rounded border-slate-300 bg-white text-sky-400 focus:ring-sky-400/40 dark:border-slate-600 dark:bg-slate-900"
        />
        Все
      </label>

      {available.map((risk) => {
        const checked = selected.has(risk.id);
        const checkboxId = `${id}-${risk.id}`;

        return (
          <label
            key={risk.id}
            htmlFor={checkboxId}
            title={risk.label}
            className={`inline-flex min-h-8 shrink-0 cursor-pointer items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] leading-5 sm:min-h-0 sm:px-1 sm:py-0 ${
              checked
                ? 'border-sky-400/40 bg-sky-400/10 text-sky-800 dark:text-sky-100'
                : 'border-slate-300 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              id={checkboxId}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => onChange(toggleRiskTypeCsv(value, risk.id))}
              className="h-2.5 w-2.5 rounded border-slate-300 bg-white text-sky-400 focus:ring-sky-400/40 dark:border-slate-600 dark:bg-slate-900"
            />
            #{risk.id}
          </label>
        );
      })}
      <span className="sr-only">{riskTypeMultiSelectLabel(selectedIds)}</span>
    </HScrollFade>
  );
}

function normalizeCsvOption(option) {
  if (option && typeof option === 'object') {
    const value = String(option.value ?? option.key ?? '').trim();
    if (!value) {
      return null;
    }
    return {
      value,
      label: String(option.label ?? option.name ?? value),
    };
  }

  const value = String(option ?? '').trim();
  if (!value) {
    return null;
  }
  return { value, label: value };
}

function CsvMultiSelect({ id, value, options, disabled, onChange, buttonClassName, emptyLabel, emptyOptionsLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedValues = parseCsvList(value);
  const selectedSet = new Set(selectedValues);
  const normalizedOptions = options.map(normalizeCsvOption).filter(Boolean);
  const labelByValue = Object.fromEntries(normalizedOptions.map((option) => [option.value, option.label]));
  const label = csvMultiSelectLabel(selectedValues, emptyLabel, labelByValue);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div id={id} ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`${buttonClassName} flex items-center justify-between gap-2 text-left`}
      >
        <span className="truncate">{label}</span>
        <span className="shrink-0 text-slate-500">{open ? '▴' : '▾'}</span>
      </button>

      {open ? (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-lg shadow-slate-300/50 dark:shadow-slate-950/40"
        >
          {normalizedOptions.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-slate-500">{emptyOptionsLabel}</p>
          ) : (
            normalizedOptions.map((option) => {
              const checkboxId = `${id}-${option.value}`;
              const checked = selectedSet.has(option.value);

              return (
                <label
                  key={option.value}
                  htmlFor={checkboxId}
                  role="option"
                  aria-selected={checked}
                  className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs ${
                    checked ? 'bg-emerald-400/10 text-emerald-800 dark:text-emerald-100' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onChange(toggleCsvListValue(value, option.value))}
                    className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-emerald-400 focus:ring-emerald-400/40"
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function FirmwareMultiSelect(props) {
  return <CsvMultiSelect emptyLabel="Все версии" emptyOptionsLabel="Нет версий" {...props} />;
}

function RiskFilters({
  filters,
  onChange,
  isLoading,
  total,
  totalUnfiltered,
  totalControllers,
  controllersSelected,
  controllersAtRisk,
  integrationsAvailable,
  organizations,
  selectedOrganizationLabel,
  firmwareVersions = [],
  controllerModels = [],
  integrationKeys = [],
  integrationLabels = {},
  filterPresets = [],
  filterPresetsLoading = false,
  activePresetQuery = '',
  onApplyPreset,
  onManagePresets,
}) {
  const selectClassName =
    'h-7 w-full min-w-0 rounded-md border border-slate-300 bg-white px-1.5 py-0 text-xs leading-7 text-slate-800 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';
  const inputClassName =
    'h-7 w-full min-w-0 rounded-md border border-slate-300 bg-white px-1.5 py-0 text-xs leading-7 text-slate-800 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';
  const integrationOptions = integrationsAvailable ? INTEGRATION_OPTIONS : [INTEGRATION_UNAVAILABLE_OPTION];
  const organizationOptions = organizations.map((organization) => ({
    value: String(organization.id),
    label: organization.label,
  }));
  const selectedFirmwareVersions = parseCsvList(filters.firmwareVersion);
  const firmwareOptions = [...firmwareVersions];
  for (const version of selectedFirmwareVersions) {
    if (version && !firmwareOptions.includes(version)) {
      firmwareOptions.push(version);
    }
  }
  const selectedControllerModels = parseCsvList(filters.controllerModel);
  const controllerModelOptions = [...controllerModels];
  for (const model of selectedControllerModels) {
    if (model && !controllerModelOptions.includes(model)) {
      controllerModelOptions.push(model);
    }
  }
  const selectedIntegrationKeys = parseCsvList(filters.integrationKeys);
  const integrationKeyValues = [...integrationKeys];
  for (const key of selectedIntegrationKeys) {
    if (key && !integrationKeyValues.includes(key)) {
      integrationKeyValues.push(key);
    }
  }
  const integrationKeyOptions = integrationKeyValues.map((key) => ({
    value: key,
    label: integrationLabels[key] || key,
  }));
  // Never fall back to page `total` (hit/case count) — that is not the org controller pool.
  const controllersCount = totalControllers ?? '—';
  const selectedCount = controllersSelected ?? totalUnfiltered ?? '—';
  const atRiskControllersCount = controllersAtRisk ?? '—';
  const showScheduleFilter = parseSelectedRiskTypes(filters.riskTypes).includes('1');

  return (
    <div
      className="space-y-2 rounded-xl border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-950/60 sm:space-y-1"
      data-testid="risks-dashboard-filters"
      data-filter-debounce-ms={FILTER_FETCH_DEBOUNCE_MS}
    >
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-1">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2.5 sm:gap-y-1">
          <p className="text-xs font-medium text-slate-800 dark:text-slate-200">Фильтры</p>
          <label className="inline-flex min-h-9 cursor-pointer items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300 sm:min-h-0">
            <input
              type="checkbox"
              checked={Boolean(filters.showAll)}
              onChange={(event) => onChange({ showAll: event.target.checked, page: 1 })}
              className="h-3.5 w-3.5 rounded border-slate-300 bg-white text-emerald-400 focus:ring-emerald-400/40 dark:border-slate-600 dark:bg-slate-900 sm:h-3 sm:w-3"
            />
            <span className="whitespace-nowrap text-slate-800 dark:text-slate-200">Показывать все</span>
            <InfoHint text="Включая устройства на связи (без просрочки). Без счётчиков, мёртвые и с битым графиком по-прежнему скрыты." />
          </label>
          <label className="inline-flex min-h-9 cursor-pointer items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300 sm:min-h-0">
            <input
              type="checkbox"
              checked={filters.hideTest !== false}
              onChange={(event) => onChange({ hideTest: event.target.checked, page: 1 })}
              className="h-3.5 w-3.5 rounded border-slate-300 bg-white text-emerald-400 focus:ring-emerald-400/40 dark:border-slate-600 dark:bg-slate-900 sm:h-3 sm:w-3"
            />
            <span className="whitespace-nowrap text-slate-800 dark:text-slate-200">Скрыть тестовые</span>
            <InfoHint text="По умолчанию скрывает устройства с «тест/test» в ФИО, адресе или серийнике." />
          </label>
          {onApplyPreset && onManagePresets ? (
            <FilterPresetsBar
              presets={filterPresets}
              activeQuery={activePresetQuery}
              loading={filterPresetsLoading}
              onApply={onApplyPreset}
              onManage={onManagePresets}
            />
          ) : null}
        </div>
        {/*
          Summary contract (docs/risks.md):
          Контроллеров = full company pool.
          Выбрано = non-risk attribute filters (firmware, schedule, …).
          В рисках = distinct controllers with ≥1 selected risk after filters.
        */}
        <p
          data-testid="dashboard-summary"
          className="max-w-full break-words text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 lg:shrink-0 lg:text-right"
        >
          {selectedOrganizationLabel ? (
            <>
              УК <span className="font-medium text-slate-800 dark:text-slate-200">{selectedOrganizationLabel}</span>
              {' · '}
            </>
          ) : null}
          Контроллеров{' '}
          <span className="font-medium text-slate-800 dark:text-slate-200">{controllersCount}</span>
          {' · '}
          Выбрано <span className="font-medium text-slate-800 dark:text-slate-200">{selectedCount}</span>
          {' · '}
          В рисках <span className="font-medium text-slate-800 dark:text-slate-200">{atRiskControllersCount}</span>
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-2 gap-y-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-[1.4fr_1.3fr_1.5fr_0.9fr_1.6fr_1.2fr_1fr_1fr] xl:gap-x-2.5 xl:gap-y-1">
        <FilterField label="Организация" className="col-span-2 min-w-0 sm:col-span-1">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={filters.organizationId}
              disabled={isLoading || organizationOptions.length === 0}
              onChange={(event) => onChange({ organizationId: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={
                organizationOptions.length > 0
                  ? organizationOptions
                  : [{ value: '', label: 'Загрузка...' }]
              }
            />
          )}
        </FilterField>

        <FilterField label="Серийник" className="col-span-2 min-w-0 sm:col-span-1">
          {(fieldId) => (
            <SerialSearchInput
              id={fieldId}
              value={filters.q}
              disabled={isLoading}
              onChange={(nextValue) => onChange({ q: nextValue, page: 1 })}
              inputClassName={inputClassName}
            />
          )}
        </FilterField>

        <FilterField label="Риск">
          {(fieldId) => (
            <RiskTypeMultiFilter
              id={fieldId}
              value={filters.riskTypes}
              disabled={isLoading}
              onChange={(nextValue) => onChange({ riskTypes: nextValue, page: 1 })}
            />
          )}
        </FilterField>

        <FilterField label="Связь">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={filters.connectionType}
              disabled={isLoading}
              onChange={(event) => onChange({ connectionType: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={CONNECTION_TYPE_OPTIONS}
            />
          )}
        </FilterField>

        <FilterField label="Модель">
          {(fieldId) => (
            <CsvMultiSelect
              id={fieldId}
              value={filters.controllerModel}
              options={controllerModelOptions}
              disabled={isLoading}
              onChange={(nextValue) => onChange({ controllerModel: nextValue, page: 1 })}
              buttonClassName={selectClassName}
              emptyLabel="Все модели"
              emptyOptionsLabel="Нет моделей"
            />
          )}
        </FilterField>

        <FilterField label="Степень">
          {(fieldId) => (
            <RiskLevelMultiFilter
              id={fieldId}
              value={filters.riskLevel}
              disabled={isLoading}
              onChange={(nextValue) => onChange({ riskLevel: nextValue, page: 1 })}
            />
          )}
        </FilterField>

        <FilterField label="Прошивка">
          {(fieldId) => (
            <FirmwareMultiSelect
              id={fieldId}
              value={filters.firmwareVersion}
              options={firmwareOptions}
              disabled={isLoading}
              onChange={(nextValue) => onChange({ firmwareVersion: nextValue, page: 1 })}
              buttonClassName={selectClassName}
            />
          )}
        </FilterField>

        {showScheduleFilter && (
          <FilterField label="График">
            {(fieldId) => (
              <FilterSelect
                id={fieldId}
                value={filters.schedule}
                disabled={isLoading}
                onChange={(event) => onChange({ schedule: event.target.value, page: 1 })}
                selectClassName={selectClassName}
                options={SCHEDULE_OPTIONS}
              />
            )}
          </FilterField>
        )}

        <FilterField label="Интеграция">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={integrationsAvailable ? filters.integration : ''}
              disabled={isLoading || !integrationsAvailable || Boolean(filters.integrationKeys)}
              onChange={(event) => onChange({ integration: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={integrationOptions}
            />
          )}
        </FilterField>

        <FilterField label="Интеграции">
          {(fieldId) => (
            <CsvMultiSelect
              id={fieldId}
              value={filters.integrationKeys}
              options={integrationKeyOptions}
              disabled={isLoading || !integrationsAvailable}
              onChange={(nextValue) =>
                onChange({
                  integrationKeys: nextValue,
                  integration: nextValue ? '' : filters.integration,
                  page: 1,
                })
              }
              buttonClassName={selectClassName}
              emptyLabel="Все интеграции"
              emptyOptionsLabel="Нет интеграций"
            />
          )}
        </FilterField>

        <FilterField label="Сортировка">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={filters.sort}
              disabled={isLoading}
              onChange={(event) => onChange({ sort: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={SORT_OPTIONS}
            />
          )}
        </FilterField>

        <FilterField label="Мёртвое">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={filters.deadDeviceMonths}
              disabled={isLoading}
              onChange={(event) => onChange({ deadDeviceMonths: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={DEAD_DEVICE_MONTH_OPTIONS.map((months) => ({
                value: String(months),
                label: formatDeadDeviceMonthOption(months),
              }))}
            />
          )}
        </FilterField>

        <FilterField label="Коммент.">
          {(fieldId) => (
            <FilterSelect
              id={fieldId}
              value={filters.hasComment || ''}
              disabled={isLoading}
              onChange={(event) => onChange({ hasComment: event.target.value, page: 1 })}
              selectClassName={selectClassName}
              options={[
                { value: '', label: 'Все' },
                { value: '1', label: 'С комментарием' },
                { value: '0', label: 'Без комментария' },
              ]}
            />
          )}
        </FilterField>

        <FilterField label="Просрочка">
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="0"
              value={filters.minOverdue}
              disabled={isLoading}
              onChange={(event) =>
                onChange({ minOverdue: event.target.value, overduePreset: 'custom', page: 1 })
              }
              className={inputClassName}
              placeholder="0"
            />
            <span className="shrink-0 text-xs text-slate-500">—</span>
            <input
              type="number"
              min="0"
              value={filters.maxOverdue}
              disabled={isLoading}
              onChange={(event) =>
                onChange({ maxOverdue: event.target.value, overduePreset: 'custom', page: 1 })
              }
              className={inputClassName}
              placeholder="∞"
            />
          </div>
        </FilterField>

        <FilterField label="Срок" className="col-span-2 min-w-0 sm:col-span-3 md:col-span-4 xl:col-span-4">
          <HScrollFade>
            {OVERDUE_PRESET_BUTTONS.map((preset) => {
              const isActive = filters.overduePreset === preset.id;

              return (
                <button
                  key={preset.id}
                  type="button"
                  disabled={isLoading}
                  onClick={() =>
                    onChange({
                      minOverdue: preset.min,
                      maxOverdue: preset.max,
                      overduePreset: preset.id,
                      page: 1,
                    })
                  }
                  className={`min-h-8 shrink-0 rounded-full border px-2.5 py-1 text-[10px] leading-5 transition disabled:opacity-60 sm:min-h-0 sm:px-1.5 sm:py-0 ${
                    isActive
                      ? 'border-sky-400/40 bg-sky-400/10 text-sky-700 dark:text-sky-200'
                      : 'border-slate-300 text-slate-500 hover:border-slate-400 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </HScrollFade>
        </FilterField>

        <div className="col-span-2 flex items-end sm:col-span-3 sm:justify-end md:col-span-4 xl:col-span-4">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => onChange({ ...DEFAULT_FILTERS, page: 1 })}
            className="min-h-10 w-full rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] font-medium text-rose-700 transition hover:border-rose-400 hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:bg-rose-500/15 sm:min-h-0 sm:w-auto sm:px-2.5 sm:py-1"
          >
            Сбросить все фильтры
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, pageSize, total, visibleCount, onPageChange, onPageSizeChange, isLoading }) {
  const totalPages = Math.max(1, Math.ceil(Number(total) / Math.max(1, Number(pageSize) || 1)));
  const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
  const rangeStart = total === 0 || visibleCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = total === 0 || visibleCount === 0 ? 0 : rangeStart + visibleCount - 1;

  const pageBtnClass =
    'h-6 rounded-md border border-slate-300 px-1.5 text-[11px] leading-none text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:border-slate-500';

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-lg border border-slate-200 bg-white px-2 py-1 dark:border-slate-800 dark:bg-slate-950/60">
      <p className="text-[11px] leading-5 text-slate-500 dark:text-slate-400">
        {rangeStart}–{rangeEnd} из {total}
      </p>

      <div className="flex items-center gap-1">
        <select
          aria-label="Размер страницы"
          title="Размер страницы"
          value={pageSize}
          disabled={isLoading}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="h-6 rounded-md border border-slate-300 bg-white px-1 text-[11px] text-slate-800 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          {PAGE_SIZE_SELECT_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <button
          type="button"
          aria-label="В начало"
          title="В начало"
          disabled={isLoading || safePage <= 1}
          onClick={() => onPageChange(1)}
          className={pageBtnClass}
        >
          «
        </button>
        <button
          type="button"
          aria-label="Назад"
          title="Назад"
          disabled={isLoading || safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className={pageBtnClass}
        >
          ‹
        </button>
        <span className="min-w-[3.25rem] px-1 text-center text-[11px] tabular-nums text-slate-700 dark:text-slate-300">
          {safePage} / {totalPages}
        </span>
        <button
          type="button"
          aria-label="Вперёд"
          title="Вперёд"
          disabled={isLoading || safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className={pageBtnClass}
        >
          ›
        </button>
        <button
          type="button"
          aria-label="В конец"
          title="В конец"
          disabled={isLoading || safePage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          className={pageBtnClass}
        >
          »
        </button>
      </div>
    </div>
  );
}

function normalizeHit(item, riskId) {
  return {
    ...item,
    risk_number: item.risk_number ?? Number(riskId),
  };
}

function mergeFacetLists(payloads, facetKey) {
  const values = new Set();

  for (const payload of payloads) {
    for (const value of payload?.facets?.[facetKey] || []) {
      values.add(value);
    }
  }

  return [...values].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
}

function mergeIntegrationLabelMaps(payloads) {
  const labels = {};
  for (const payload of payloads) {
    Object.assign(labels, payload?.facets?.integration_labels || {});
  }
  return labels;
}

function mergeFirmwareFacets(payloads) {
  return mergeFacetLists(payloads, 'firmware_versions');
}

export default function DashboardPage({ activeTab = 'dashboard', onTabChange } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, filters } = useMemo(() => parseDashboardQuery(searchParams), [searchParams]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [integrationsAvailable, setIntegrationsAvailable] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [deviceHistoryTarget, setDeviceHistoryTarget] = useState(null);
  const [commentsTarget, setCommentsTarget] = useState(null);
  const [filterPresets, setFilterPresets] = useState([]);
  const [filterPresetsLoading, setFilterPresetsLoading] = useState(true);
  const [presetsModalOpen, setPresetsModalOpen] = useState(false);
  const [expandedCaseKeys, setExpandedCaseKeys] = useState([]);
  const [detailCase, setDetailCase] = useState(null);
  const integrationsAvailableRef = useRef(false);
  const organizationsInitializedRef = useRef(false);
  const loadRequestIdRef = useRef(0);
  const loadAbortRef = useRef(null);
  const lastFetchedFiltersKeyRef = useRef('');
  const loadedQueryRef = useRef('');
  const orgsLoadedRef = useRef(false);
  const dashboardVisibleRef = useRef(false);
  const cachedAllCasesRef = useRef(null);

  const activePresetQuery = useMemo(
    () => serializeDashboardQuery({ page, pageSize, filters }),
    [page, pageSize, filters],
  );

  const loadFilterPresets = useCallback(async () => {
    setFilterPresetsLoading(true);
    try {
      const items = await listFilterPresets();
      setFilterPresets(items);
    } catch {
      setFilterPresets([]);
    } finally {
      setFilterPresetsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFilterPresets();
  }, [loadFilterPresets]);

  const handleApplyPreset = useCallback(
    (preset) => {
      if (activeTab !== 'dashboard') {
        return;
      }
      const raw = String(preset.query || '').trim().replace(/^\?/, '');
      const params = new URLSearchParams(raw);
      params.set('tab', 'dashboard');
      setSearchParams(params, { replace: true });
    },
    [activeTab, setSearchParams],
  );

  const applyQueryState = useCallback(
    (nextPage, nextPageSize, nextFilters, { scroll = false } = {}) => {
      // Never rewrite the URL while another sticky tab is active.
      if (activeTab !== 'dashboard') {
        return;
      }
      const params = buildDashboardSearchParams({
        page: nextPage,
        pageSize: nextPageSize,
        filters: nextFilters,
      });
      params.set('tab', 'dashboard');
      setSearchParams(params, { replace: true });

      if (scroll) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [activeTab, setSearchParams],
  );

  const loadRisks = useCallback(
    async (query) => {
      const selectedRiskIds = parseSelectedRiskTypes(query.riskTypes || DEFAULT_FILTERS.riskTypes);

      if (selectedRiskIds.length === 0) {
        setError('Нет доступных типов риска');
        setData(null);
        setIsLoading(false);
        return;
      }

      if (loadAbortRef.current) {
        loadAbortRef.current.abort();
      }
      const abortController = new AbortController();
      loadAbortRef.current = abortController;
      const requestId = ++loadRequestIdRef.current;

      setIsLoading(true);
      setError('');
      const wasIntegrationsAvailable = integrationsAvailableRef.current;
      // Schedule (daily/weekly/monthly) is risk#1 connection cadence only.
      // Do not merge risk#2 transmission-day hits into that filter (Sergey #21499).
      const fetchRiskIds = query.schedule
        ? selectedRiskIds.filter((riskId) => String(riskId) === '1')
        : selectedRiskIds;
      const effectiveRiskIds = fetchRiskIds.length > 0 ? fetchRiskIds : selectedRiskIds;
      const isMultiRisk = effectiveRiskIds.length > 1;
      const fetchPage = isMultiRisk ? 1 : query.page;
      const fetchPageSize = isMultiRisk ? MULTI_RISK_FETCH_CAP : query.pageSize;

      try {
        const payloads = await Promise.all(
          effectiveRiskIds.map(async (riskId) => {
            const qs = buildRiskQuery({
              ...query,
              page: fetchPage,
              pageSize: fetchPageSize,
              // B7: never second-fetch via __integrationRetry; sticky cache + ref is enough.
              integrationsAvailable: Boolean(query.integration) || wasIntegrationsAvailable,
            });
            const cacheKey = `riskContext:${riskId}:${qs}`;
            if (query.__forceRefresh) {
              cacheInvalidate(cacheKey);
            }
            return cacheFetch(cacheKey, async () => {
              const response = await apiFetch(`/api/risk/${riskId}/context?${qs}`, {
                signal: abortController.signal,
              });
              const payload = await readJsonResponse(response, `Risk #${riskId}`);
              if (!response.ok) {
                throw new Error(payload.message || `HTTP ${response.status}`);
              }
              return payload;
            });
          }),
        );

        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const primaryPayload = payloads[0];
        const allHits = payloads.flatMap((payload, index) =>
          (payload.items || []).map((item) => normalizeHit(item, effectiveRiskIds[index])),
        );
        const hitCountsByRisk = Object.fromEntries(
          effectiveRiskIds.map((riskId, index) => [riskId, payloads[index]?.total ?? 0]),
        );
        const groupedCases = buildRiskCases(allHits);
        // Cache always stores grouped cases (one card / controller). Flat rows are table-only.
        const displayCases = casesForDashboardView(groupedCases, {
          isMultiRisk,
          viewMode: query.viewMode || DEFAULT_FILTERS.viewMode,
          riskNumbers: effectiveRiskIds,
        });
        const displayTotal = isMultiRisk
          ? displayCases.length
          : (primaryPayload.total ?? displayCases.length);
        const safePageSize = Math.max(1, Number(query.pageSize) || PAGE_SIZE_OPTIONS[0]);
        const totalPages = Math.max(1, Math.ceil(displayTotal / safePageSize));
        const requestedPage = Math.max(1, Number(query.page) || 1);

        // Only clamp when page is past the end — never jump forward to last page.
        if (displayTotal > 0 && requestedPage > totalPages) {
          if (requestId !== loadRequestIdRef.current) {
            return;
          }
          applyQueryState(totalPages, safePageSize, {
            riskTypes: query.riskTypes,
            sort: query.sort,
            schedule: query.schedule,
            integration: query.integration,
            minOverdue: query.minOverdue,
            maxOverdue: query.maxOverdue,
            overduePreset: query.overduePreset,
            deadDeviceMonths: query.deadDeviceMonths,
            riskLevel: query.riskLevel,
            firmwareVersion: query.firmwareVersion,
            controllerModel: query.controllerModel,
            integrationKeys: query.integrationKeys,
            connectionType: query.connectionType,
            q: query.q,
            viewMode: query.viewMode,
            showAll: query.showAll,
            hideTest: query.hideTest,
            hasComment: query.hasComment,
            organizationId: query.organizationId,
          });
          return;
        }

        const available = payloads.some((payload) => payload.diagnostics?.integrations_available === true);
        integrationsAvailableRef.current = available;
        setIntegrationsAvailable(available);

        // Do not second-fetch the whole multi-risk payload when integration filter turns on.
        // Backend accepts integrationsAvailable=true once we know the org has integrations.

        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const pageForCases = Math.min(requestedPage, totalPages);
        const cases = isMultiRisk
          ? paginateCases(displayCases, pageForCases, safePageSize)
          : displayCases;
        const controllerSummary = mergeControllerSummary(payloads);

        setData({
          allCases: isMultiRisk ? groupedCases : null,
          cases,
          total: displayTotal,
          total_at_risk: primaryPayload.total_at_risk,
          controllers_at_risk: controllerSummary.controllers_at_risk,
          controllers_selected: controllerSummary.controllers_selected,
          total_unfiltered: primaryPayload.total_unfiltered,
          total_controllers:
            controllerSummary.organization_controller_total ??
            primaryPayload.total_controllers ??
            primaryPayload.organization_controller_total,
          controllers_with_connection_history: primaryPayload.controllers_with_connection_history,
          organization: primaryPayload.organization,
          organization_controller_total: controllerSummary.organization_controller_total,
          organization_integration_total: primaryPayload.organization_integration_total,
          filters: primaryPayload.filters,
          facets: {
            firmware_versions: mergeFirmwareFacets(payloads),
            controller_models: mergeFacetLists(payloads, 'controller_models'),
            integration_keys: mergeFacetLists(payloads, 'integration_keys'),
            integration_labels: mergeIntegrationLabelMaps(payloads),
          },
          diagnostics: {
            ...primaryPayload.diagnostics,
            hit_counts_by_risk: hitCountsByRisk,
            payloads: payloads.map((payload, index) => ({
              risk_id: selectedRiskIds[index],
              diagnostics: payload.diagnostics,
            })),
          },
          hitCountsByRisk,
          clientPaginated: isMultiRisk,
          filtersKey: serializeDashboardFetchKey({
            page: 1,
            pageSize: PAGE_SIZE_OPTIONS[0],
            filters: {
              riskTypes: query.riskTypes,
              sort: query.sort,
              schedule: query.schedule,
              integration: query.integration,
              minOverdue: query.minOverdue,
              maxOverdue: query.maxOverdue,
              overduePreset: query.overduePreset,
              deadDeviceMonths: query.deadDeviceMonths,
              riskLevel: query.riskLevel,
              firmwareVersion: query.firmwareVersion,
              controllerModel: query.controllerModel,
              integrationKeys: query.integrationKeys,
              connectionType: query.connectionType,
              q: query.q,
              viewMode: query.viewMode,
              showAll: query.showAll,
              hideTest: query.hideTest,
              hasComment: query.hasComment,
              organizationId: query.organizationId,
            },
          }),
        });
        if (isMultiRisk) {
          cachedAllCasesRef.current = groupedCases;
          lastFetchedFiltersKeyRef.current = serializeDashboardFetchKey({
            page: 1,
            pageSize: PAGE_SIZE_OPTIONS[0],
            filters: {
              riskTypes: query.riskTypes,
              sort: query.sort,
              schedule: query.schedule,
              integration: query.integration,
              minOverdue: query.minOverdue,
              maxOverdue: query.maxOverdue,
              overduePreset: query.overduePreset,
              deadDeviceMonths: query.deadDeviceMonths,
              riskLevel: query.riskLevel,
              firmwareVersion: query.firmwareVersion,
              controllerModel: query.controllerModel,
              integrationKeys: query.integrationKeys,
              connectionType: query.connectionType,
              q: query.q,
              viewMode: query.viewMode,
              showAll: query.showAll,
              hideTest: query.hideTest,
              hasComment: query.hasComment,
              organizationId: query.organizationId,
            },
          });
        } else {
          cachedAllCasesRef.current = null;
          lastFetchedFiltersKeyRef.current = '';
        }
        return true;
      } catch (loadError) {
        if (loadError?.name === 'AbortError' || requestId !== loadRequestIdRef.current) {
          return false;
        }
        setError(loadError.message || 'Не удалось загрузить риски');
        return false;
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [applyQueryState],
  );

  useEffect(() => {
    if (activeTab !== 'dashboard' || orgsLoadedRef.current) {
      return undefined;
    }
    let cancelled = false;

    async function loadOrganizations() {
      try {
        const payload = await cacheFetch('organizations', async () => {
          const response = await apiFetch('/api/organizations');
          const body = await readJsonResponse(response, 'Organizations');
          if (!response.ok) {
            throw new Error('Organizations request failed');
          }
          return body;
        });

        if (cancelled) {
          return;
        }

        const items = payload.items || [];
        setOrganizations(items);
        orgsLoadedRef.current = true;

        if (
          activeTab === 'dashboard' &&
          !organizationsInitializedRef.current &&
          items.length > 0 &&
          !filters.organizationId
        ) {
          organizationsInitializedRef.current = true;
          // Preserve the current URL page — only inject organizationId.
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.set('organizationId', String(items[0].id));
              next.set('tab', 'dashboard');
              return next;
            },
            { replace: true },
          );
        }
      } catch {
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    }

    loadOrganizations();

    return () => {
      cancelled = true;
    };
    // Orgs once — do not re-fetch when organizationId is set from the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, setSearchParams]);

  const filtersKey = useMemo(
    () =>
      serializeDashboardFetchKey({
        page: 1,
        pageSize: PAGE_SIZE_OPTIONS[0],
        filters,
      }),
    [filters],
  );

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      dashboardVisibleRef.current = false;
      return undefined;
    }

    const selectedRiskIds = parseSelectedRiskTypes(filters.riskTypes || DEFAULT_FILTERS.riskTypes);
    const isMultiRisk = selectedRiskIds.length > 1;
    const viewMode = filters.viewMode || DEFAULT_FILTERS.viewMode;

    // Multi-risk: page/pageSize/viewMode changes are local — do not refetch.
    if (
      isMultiRisk &&
      lastFetchedFiltersKeyRef.current === filtersKey &&
      Array.isArray(cachedAllCasesRef.current)
    ) {
      dashboardVisibleRef.current = true;
      const groupedCases = cachedAllCasesRef.current;
      const displayCases = casesForDashboardView(groupedCases, {
        isMultiRisk: true,
        viewMode,
        riskNumbers: selectedRiskIds,
      });
      setData((previous) => ({
        ...(previous || {}),
        allCases: groupedCases,
        cases: paginateCases(displayCases, page, pageSize),
        total: displayCases.length,
        clientPaginated: true,
        filtersKey,
      }));
      return;
    }

    // Returning to the tab with the same query must not refetch. Filter edits still do.
    const justShown = !dashboardVisibleRef.current;
    dashboardVisibleRef.current = true;
    const queryStamp = `${filtersKey}|${page}|${pageSize}|${viewMode}`;
    if (justShown && loadedQueryRef.current === queryStamp) {
      return undefined;
    }

    // Debounce filter churn — rapid chip/select changes were stampeding the backend.
    // CRA has no import.meta.env — use NODE_ENV for test skip.
    const delayMs =
      typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test'
        ? 0
        : lastFetchedFiltersKeyRef.current === filtersKey
          ? 0
          : FILTER_FETCH_DEBOUNCE_MS;
    const timer = setTimeout(() => {
      loadRisks({ page, pageSize, ...filters, integrationsAvailable: integrationsAvailableRef.current }).then(
        (ok) => {
          if (ok) {
            loadedQueryRef.current = queryStamp;
          }
        },
      );
    }, delayMs);
    return () => clearTimeout(timer);
  }, [activeTab, loadRisks, page, pageSize, filtersKey, filters.viewMode]);

  const handleFiltersChange = (patch) => {
    const { page: nextPage = 1, ...filterPatch } = patch;
    const nextFilters = { ...filters, ...filterPatch };
    applyQueryState(nextPage, pageSize, nextFilters, { scroll: Boolean(patch.page) });
  };

  const handlePageChange = (nextPage) => {
    const parsed = Number(nextPage);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    applyQueryState(Math.floor(parsed), pageSize, filters);
  };

  const handlePageSizeChange = (nextPageSize) => {
    applyQueryState(1, nextPageSize, filters);
  };

  const cases = data?.cases || [];
  const diagnostics = data?.diagnostics;
  const total = data?.total ?? 0;
  const showSkeleton = useSkeletonVisibility(isLoading);
  const { openClientLk, loadingUserId } = useOpenClientLk();
  const selectedOrganization =
    organizations.find((organization) => String(organization.id) === String(filters.organizationId)) ||
    data?.organization ||
    null;
  const selectedOrganizationLabel = selectedOrganization?.label || selectedOrganization?.description || null;
  const viewMode = filters.viewMode || DEFAULT_FILTERS.viewMode;

  const handleToggleExpandedCase = useCallback((caseKey) => {
    setExpandedCaseKeys((current) =>
      current.includes(caseKey) ? current.filter((key) => key !== caseKey) : [...current, caseKey],
    );
  }, []);

  const handleToggleComments = useCallback((riskCase) => {
    setCommentsTarget((current) => {
      if (current?.caseKey === riskCase.key) {
        return null;
      }

      return {
        caseKey: riskCase.key,
        deviceId: resolveCommentDeviceId(riskCase),
        serialNumber: commentDeviceLabel(riskCase),
      };
    });
  }, []);

  return (
    <section className="space-y-4 sm:space-y-6">
      <RisksTopBar
        actions={
          <>
            <div className="inline-flex w-full rounded-xl border border-slate-300 p-1 dark:border-slate-700 sm:w-auto">
              <button
                type="button"
                disabled={isLoading}
                onClick={() => handleFiltersChange({ viewMode: 'table' })}
                className={`min-h-10 flex-1 rounded-lg px-3 py-2 text-sm transition sm:min-h-0 sm:flex-none sm:py-1.5 ${
                  viewMode === 'table'
                    ? 'bg-sky-400/15 text-sky-800 dark:text-sky-100'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                Таблица
              </button>
              <button
                type="button"
                disabled={isLoading}
                onClick={() => handleFiltersChange({ viewMode: 'cards' })}
                className={`min-h-10 flex-1 rounded-lg px-3 py-2 text-sm transition sm:min-h-0 sm:flex-none sm:py-1.5 ${
                  viewMode === 'cards'
                    ? 'bg-sky-400/15 text-sky-800 dark:text-sky-100'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                Карточки
              </button>
            </div>
            <button
              type="button"
              onClick={() =>
                loadRisks({ page, pageSize, ...filters, integrationsAvailable: integrationsAvailableRef.current })
              }
              disabled={isLoading}
              className="min-h-10 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:border-slate-500 sm:min-h-0"
            >
              {isLoading ? 'Обновляем...' : 'Обновить'}
            </button>
          </>
        }
      />

      <RiskFilters
        filters={filters}
        onChange={handleFiltersChange}
        isLoading={isLoading}
        total={total}
        totalUnfiltered={data?.total_unfiltered}
        totalControllers={
          data?.organization_controller_total ??
          data?.total_controllers ??
          diagnostics?.raw_rows
        }
        controllersSelected={data?.controllers_selected}
        controllersAtRisk={data?.controllers_at_risk}
        integrationsAvailable={integrationsAvailable}
        organizations={organizations}
        selectedOrganizationLabel={selectedOrganizationLabel}
        firmwareVersions={data?.facets?.firmware_versions || []}
        controllerModels={data?.facets?.controller_models || []}
        integrationKeys={data?.facets?.integration_keys || []}
        integrationLabels={data?.facets?.integration_labels || {}}
        filterPresets={filterPresets}
        filterPresetsLoading={filterPresetsLoading}
        activePresetQuery={activePresetQuery}
        onApplyPreset={handleApplyPreset}
        onManagePresets={() => setPresetsModalOpen(true)}
      />

      {presetsModalOpen ? (
        <FilterPresetsModal
          presets={filterPresets}
          currentQuery={activePresetQuery}
          onClose={() => setPresetsModalOpen(false)}
          onChanged={loadFilterPresets}
        />
      ) : null}

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          visibleCount={cases.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
        />
      )}

      {error && <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-red-700 dark:text-red-200">{error}</div>}

      {!error && showSkeleton && (
        <DashboardRisksSkeleton count={Math.min(pageSize, 10)} viewMode={viewMode} />
      )}

      {!error && !showSkeleton && cases.length === 0 && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-emerald-700 dark:text-emerald-200">
          Активных рисков не найдено.
        </div>
      )}

      {!error && !showSkeleton && cases.length > 0 && viewMode === 'table' && (
        <div className="content-reveal">
          <RiskCasesTable
            cases={cases}
            organizationLabel={selectedOrganizationLabel}
            summaryRiskNumbers={parseSelectedRiskTypes(filters.riskTypes || DEFAULT_FILTERS.riskTypes)}
            expandedKeys={expandedCaseKeys}
            onToggleExpanded={handleToggleExpandedCase}
            onOpenDetails={setDetailCase}
            openCommentsKey={commentsTarget?.caseKey ?? null}
            onToggleComments={handleToggleComments}
            onOpenDeviceHistory={setDeviceHistoryTarget}
          />
        </div>
      )}

      {!error && !showSkeleton && cases.length > 0 && viewMode === 'cards' && (
        <div className="content-reveal grid auto-rows-fr items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3 [&>*]:h-full [&>*]:min-h-[40rem]">
          {cases.map((riskCase) => {
            const commentsOpen = commentsTarget?.caseKey === riskCase.key;

            return (
              <RiskCaseCard
                key={riskCase.key}
                riskCase={riskCase}
                organizationLabel={selectedOrganizationLabel}
                onOpenClientLk={openClientLk}
                openingUserId={loadingUserId}
                onOpenHistory={(facts) =>
                  setHistoryTarget({
                    deviceId: facts.device_id ?? facts.counter_id,
                    serialNumber: facts.serial_number || facts.counter_serial_number,
                    riskNumber: facts.risk_number || 1,
                    integrationId: facts.integration_id ?? null,
                  })
                }
                commentsOpen={commentsOpen}
                onToggleComments={handleToggleComments}
              />
            );
          })}
        </div>
      )}

      {detailCase ? (
        <RiskCaseDetailModal
          riskCase={detailCase}
          organizationLabel={selectedOrganizationLabel}
          onClose={() => setDetailCase(null)}
          onOpenClientLk={openClientLk}
          openingUserId={loadingUserId}
          onOpenHistory={(facts) =>
            setHistoryTarget({
              deviceId: facts.device_id ?? facts.counter_id,
              serialNumber: facts.serial_number || facts.counter_serial_number,
              riskNumber: facts.risk_number || 1,
              integrationId: facts.integration_id ?? null,
            })
          }
          commentsOpen={commentsTarget?.caseKey === detailCase.key}
          onToggleComments={handleToggleComments}
        />
      ) : null}

      {historyTarget && (
        <RiskHistoryModal
          deviceId={historyTarget.deviceId}
          serialNumber={historyTarget.serialNumber}
          riskNumber={historyTarget.riskNumber}
          integrationId={historyTarget.integrationId}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {deviceHistoryTarget && (
        <DeviceHistoryModal
          deviceId={deviceHistoryTarget.deviceId}
          serialNumber={deviceHistoryTarget.serialNumber}
          onClose={() => setDeviceHistoryTarget(null)}
        />
      )}

      {commentsTarget && (
        <DeviceCommentsModal
          deviceId={commentsTarget.deviceId}
          serialNumber={commentsTarget.serialNumber}
          onClose={() => setCommentsTarget(null)}
        />
      )}

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          visibleCount={cases.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading}
        />
      )}

    </section>
  );
}
